import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Footer from "@/components/Footer";

const ease = [0.22, 1, 0.36, 1];

export default function LegalLayout({ overline, title, intro, updated, sections, testId }) {
  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div data-testid={testId}>
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10" data-testid="legal-header">
        <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="legal-logo-link">
            <img src="/agentica-mark.png" alt="Agentica AI Solutions logo" className="h-14 w-auto transition-transform duration-300 group-hover:scale-105" />
            <span className="font-display text-lg font-700 tracking-tight text-white">
              Agentica<span className="bg-gradient-to-r from-[#4f6bff] to-[#9d6bff] bg-clip-text text-transparent"> AI</span>
            </span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors duration-300" data-testid="legal-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
        </nav>
      </header>

      <main className="relative z-[2] pt-40 pb-24 lg:pb-32" data-testid="legal-main">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-gradient-to-r from-[#4f6bff] to-[#9d6bff]" />
            <span className="overline text-[#8b7cff]/90">{overline}</span>
          </div>
          <h1 className="font-display font-700 tracking-tighter leading-[0.95] text-white text-4xl sm:text-5xl lg:text-6xl max-w-4xl" data-testid="legal-title">
            {title.split(" ").map((w, i) => (
              <span key={i} className="inline-block overflow-hidden mr-[0.25em] align-bottom">
                <motion.span className="inline-block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.08 * i, ease }}>
                  {w}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4, ease }}
            className="text-white/60 leading-relaxed max-w-2xl mt-8 text-base">
            {intro}
          </motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.6 }}
            className="overline text-white/35 mt-6" data-testid="legal-updated">
            Last updated — {updated}
          </motion.p>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 mt-20 lg:mt-28">
            <aside className="hidden lg:block lg:col-span-4">
              <div className="sticky top-32">
                <p className="overline text-white/40 mb-5">Contents</p>
                <ol className="space-y-3 border-l border-white/10">
                  {sections.map((s, i) => (
                    <li key={s.id}>
                      <button onClick={() => jump(s.id)} className="group flex items-baseline gap-3 pl-5 -ml-px border-l border-transparent hover:border-[#9d6bff] text-left text-sm text-white/50 hover:text-white transition-colors duration-300"
                        data-testid={`legal-toc-${s.id}`}>
                        <span className="font-display text-xs text-[#8b7cff]/70 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                        {s.title}
                      </button>
                    </li>
                  ))}
                </ol>
              </div>
            </aside>

            <article className="lg:col-span-8 space-y-16" data-testid="legal-content">
              {sections.map((s, i) => (
                <Reveal key={s.id} id={s.id} className="scroll-mt-32" data-testid={`legal-section-${s.id}`}>
                  <div className="flex items-baseline gap-4 mb-5">
                    <span className="font-display text-sm text-[#8b7cff]/80 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                    <h2 className="font-display font-700 tracking-tight text-white text-2xl sm:text-3xl">{s.title}</h2>
                  </div>
                  <div className="legal-body text-white/60 text-sm sm:text-base leading-relaxed space-y-4 pl-0 sm:pl-9">
                    {s.body}
                  </div>
                </Reveal>
              ))}

              <Reveal className="rounded-3xl border border-white/10 bg-white/[0.02] p-8 sm:p-10">
                <p className="overline text-[#8b7cff]/90 mb-3">Questions?</p>
                <p className="text-white/60 text-sm leading-relaxed mb-6">
                  Reach our privacy and legal team at{" "}
                  <a href="mailto:ikumar@agentica-sol.com" className="text-white underline underline-offset-4 decoration-white/30 hover:decoration-white transition-colors">ikumar@agentica-sol.com</a>{" "}
                  or call <a href="tel:+17542182448" className="text-white">+1 754 218 2448</a>.
                </p>
                <Link to="/" className="group inline-flex items-center gap-1.5 rounded-full bg-white text-void px-5 py-2.5 text-sm font-500 hover:bg-gradient-to-r hover:from-[#4f6bff] hover:to-[#9d6bff] hover:text-white transition-[background-color,color] duration-300" data-testid="legal-home-cta">
                  Return home
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </Reveal>
            </article>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
